import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "sms-header__text" }
const _hoisted_2 = { class: "sms-header__icon-title" }
const _hoisted_3 = { class: "sms-header__icon-container" }
const _hoisted_4 = { class: "sms-field" }
const _hoisted_5 = {
  key: 0,
  class: "sms-no-phones"
}
const _hoisted_6 = { class: "sms-field" }
const _hoisted_7 = {
  key: 0,
  class: "sms-no-phones"
}
const _hoisted_8 = {
  key: 0,
  class: "sms-selected-phone"
}
const _hoisted_9 = { class: "sms-field" }
const _hoisted_10 = { class: "sms-ai-row" }
const _hoisted_11 = ["disabled", "title"]
const _hoisted_12 = ["disabled", "title"]

import { Button, Modal, Select } from "@/components/ui";
import type { SelectOption } from "@/components/ui/types";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import { Icon } from "@iconify/vue";
import { computed, ref, watch } from "vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";

interface OrgPhoneNumber {
  id: string;
  number: string;
  region?: string;
  city?: string;
}

interface ContactPhone {
  id: string;
  label: string;
  number: string;
}

interface Props {
  show: boolean;
  orgPhoneNumbers: OrgPhoneNumber[];
  contactPhoneNumbers: ContactPhone[];
  contactId: string;
  isLoading?: boolean;
  initialMessage?: string;
  icpId?: string;
}

interface CreateSMSChatRequest {
  contactId: string;
  phoneNumberId: string;
  contactPhoneNumber: string;
  contactPhoneId?: string;
  message?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'StartSMSChatModal',
  props: {
    show: { type: Boolean },
    orgPhoneNumbers: { default: () => [] },
    contactPhoneNumbers: { default: () => [] },
    contactId: {},
    isLoading: { type: Boolean, default: false },
    initialMessage: { default: "" },
    icpId: {}
  },
  emits: ["close", "create-chat"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $toast = useToast();

// Form data
const formData = ref({
  phoneNumberId: "",
  contactPhoneId: "",
  contactPhoneNumber: "",
  message: "",
});

const errors = ref<{
  phoneNumberId?: string;
  contactPhoneId?: string;
}>({});

const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const isGeneratingAiCompose = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const resolvedAiPromptsQuery = useResolvedAiPrompts();

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const toggleAiComposePanel = () => {
  showAiPanel.value = false;
  showAiComposePanel.value = !showAiComposePanel.value;
};

const handleAiComposeGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCompose.value) return;
  isGeneratingAiCompose.value = true;
  try {
    const response = await AIGenerationService.generateCompose({
      contactId: props.contactId,
      icpId: props.icpId,
      channel: "sms",
      customInstructions: payload.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });

    if (response.body) {
      formData.value.message = response.body;
      showAiComposePanel.value = false;
      $toast?.success("Message composed successfully");
    }
  } catch (error: any) {
    console.error("Failed to compose message:", error);
    $toast?.error(error.message || "Failed to compose message");
  } finally {
    isGeneratingAiCompose.value = false;
  }
};

const handleAiCustomizationGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId,
      icpId: props.icpId,
      channel: "sms",
      currentDraft: formData.value.message || "",
      customInstructions: payload.customInstructions,
    });

    if (response.body) {
      formData.value.message = response.body;
      showAiPanel.value = false;
      $toast?.success("Message refined successfully");
    }
  } catch (error: any) {
    console.error("Failed to refine message:", error);
    $toast?.error(error.message || "Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

// Organization phone number options
const orgPhoneOptions = computed<SelectOption[]>(() => {
  return props.orgPhoneNumbers.map((phone) => ({
    value: phone.id,
    label: phone.city ? `${phone.number} (${phone.city})` : phone.number,
  }));
});

const contactPhoneOptions = computed<SelectOption[]>(() => {
  return props.contactPhoneNumbers.map((phone) => ({
    value: phone.id,
    label: `${phone.label}: ${phone.number}`,
  }));
});

// Check if form can be submitted
const canSubmit = computed(() => {
  return (
    formData.value.phoneNumberId &&
    formData.value.contactPhoneNumber &&
    props.orgPhoneNumbers.length > 0 &&
    props.contactPhoneNumbers.length > 0 &&
    !errors.value.phoneNumberId &&
    !errors.value.contactPhoneId
  );
});

const handleContactPhoneSelect = (value: string | number) => {
  const phoneId = String(value);
  const phone = props.contactPhoneNumbers.find((p) => p.id === phoneId);
  if (phone) {
    formData.value.contactPhoneNumber = phone.number;
    formData.value.contactPhoneId = phone.id;
  }
};

const handleSubmit = () => {
  errors.value = {};

  if (!formData.value.phoneNumberId) {
    errors.value.phoneNumberId = "Please select a phone number to send from";
    return;
  }

  if (!formData.value.contactPhoneNumber) {
    errors.value.contactPhoneId = "Please select a phone number to send to";
    return;
  }

  const request: CreateSMSChatRequest = {
    contactId: props.contactId,
    phoneNumberId: formData.value.phoneNumberId,
    contactPhoneNumber: formData.value.contactPhoneNumber,
    contactPhoneId: formData.value.contactPhoneId || undefined,
    message: formData.value.message?.trim() || undefined,
  };

  emit("create-chat", request);
};

// Auto-select if only one option
watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      // Auto-select org phone number if only one
      if (props.orgPhoneNumbers.length === 1) {
        formData.value.phoneNumberId = props.orgPhoneNumbers[0].id;
      }

      if (props.contactPhoneNumbers.length === 1) {
        const phone = props.contactPhoneNumbers[0];
        formData.value.contactPhoneId = phone.id;
        formData.value.contactPhoneNumber = phone.number;
      }

      if (props.initialMessage) {
        formData.value.message = props.initialMessage;
      }
    } else {
      // Reset form when modal closes
      formData.value = {
        phoneNumberId: "",
        contactPhoneId: "",
        contactPhoneNumber: "",
        message: "",
      };
      errors.value = {};
      showAiPanel.value = false;
      showAiComposePanel.value = false;
    }
  },
  { immediate: true }
);

// Watch for changes in phone numbers to auto-select
watch(
  () => [props.orgPhoneNumbers, props.contactPhoneNumbers],
  () => {
    if (props.show) {
      if (props.orgPhoneNumbers.length === 1 && !formData.value.phoneNumberId) {
        formData.value.phoneNumberId = props.orgPhoneNumbers[0].id;
      }
      if (props.contactPhoneNumbers.length === 1 && !formData.value.contactPhoneId) {
        const phone = props.contactPhoneNumbers[0];
        formData.value.contactPhoneId = phone.id;
        formData.value.contactPhoneNumber = phone.number;
      }
    }
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Modal), {
    show: __props.show,
    size: "md",
    dialogMaxWidth: "640px",
    closeOnBackdrop: false,
    blurBackdrop: false,
    backdropOpacity: 0.5,
    zIndex: 10060,
    "show-close-button": false,
    onClose: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('close')))
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(Icon), {
              icon: "mdi:message-text-outline",
              class: "sms-header__icon"
            })
          ]),
          _cache[8] || (_cache[8] = _createElementVNode("div", null, [
            _createElementVNode("h2", { class: "sms-header__title" }, "Start New SMS Chat"),
            _createElementVNode("p", { class: "sms-header__subtitle" }, " Select which phone numbers to use for this conversation ")
          ], -1))
        ])
      ]),
      _createElementVNode("button", {
        type: "button",
        class: "sms-header__close",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
        "aria-label": "Close"
      }, [
        _createVNode(_unref(Icon), {
          icon: "mdi:close",
          width: "18",
          height: "18"
        })
      ])
    ]),
    body: _withCtx(() => [
      _createElementVNode("form", {
        id: "sms-chat-form",
        onSubmit: _withModifiers(handleSubmit, ["prevent"]),
        class: "sms-form"
      }, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(Select), {
            modelValue: formData.value.phoneNumberId,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.phoneNumberId) = $event)),
            label: "Send From",
            placeholder: "Select your phone number",
            options: orgPhoneOptions.value,
            error: errors.value.phoneNumberId,
            required: ""
          }, null, 8, ["modelValue", "options", "error"]),
          (__props.orgPhoneNumbers.length === 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_5, [
                _createVNode(_unref(Icon), { icon: "mdi:alert-circle-outline" }),
                _cache[9] || (_cache[9] = _createTextVNode(" No phone numbers available. Please purchase a Twilio number first. ", -1))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_unref(Select), {
            modelValue: formData.value.contactPhoneId,
            "onUpdate:modelValue": [
              _cache[2] || (_cache[2] = ($event: any) => ((formData.value.contactPhoneId) = $event)),
              handleContactPhoneSelect
            ],
            label: "Send To",
            placeholder: "Select contact's phone number",
            options: contactPhoneOptions.value,
            error: errors.value.contactPhoneId,
            required: ""
          }, null, 8, ["modelValue", "options", "error"]),
          (__props.contactPhoneNumbers.length === 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_7, [
                _createVNode(_unref(Icon), { icon: "mdi:alert-circle-outline" }),
                _cache[10] || (_cache[10] = _createTextVNode(" This contact has no phone numbers. Please add a phone number first. ", -1))
              ]))
            : _createCommentVNode("", true)
        ]),
        (formData.value.contactPhoneNumber)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(_unref(Icon), { icon: "mdi:phone-outline" }),
              _createElementVNode("span", null, _toDisplayString(formData.value.contactPhoneNumber), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_9, [
          _cache[12] || (_cache[12] = _createElementVNode("label", { class: "sms-label" }, [
            _createTextVNode(" Message "),
            _createElementVNode("span", { class: "sms-optional" }, "(Optional)")
          ], -1)),
          _withDirectives(_createElementVNode("textarea", {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.message) = $event)),
            class: "sms-textarea",
            placeholder: "Enter your message here...",
            rows: "4"
          }, null, 512), [
            [_vModelText, formData.value.message]
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("button", {
              ref_key: "aiComposeButtonRef",
              ref: aiComposeButtonRef,
              type: "button",
              class: "sms-ai-btn sms-ai-btn--compose",
              disabled: isGeneratingAiCompose.value,
              title: isGeneratingAiCompose.value ? 'Generating...' : 'AI Compose',
              onClick: toggleAiComposePanel
            }, [
              _createVNode(_unref(Icon), {
                icon: "mdi:sparkles",
                class: "sms-ai-btn-icon"
              }),
              _createElementVNode("span", null, _toDisplayString(isGeneratingAiCompose.value ? "Generating..." : "AI Compose"), 1)
            ], 8, _hoisted_11),
            _createElementVNode("button", {
              ref_key: "aiRefineButtonRef",
              ref: aiRefineButtonRef,
              type: "button",
              class: "sms-ai-btn",
              disabled: isGeneratingAiCustom.value,
              title: isGeneratingAiCustom.value ? 'Generating...' : 'AI Refine',
              onClick: toggleAiRefinePanel
            }, [
              _createVNode(_unref(Icon), {
                icon: "mdi:sparkles",
                class: "sms-ai-btn-icon"
              }),
              _cache[11] || (_cache[11] = _createElementVNode("span", null, "AI Refine", -1))
            ], 8, _hoisted_12)
          ]),
          _createVNode(AiRefinePopover, {
            show: showAiPanel.value,
            "trigger-el": aiRefineButtonRef.value,
            disabled: isGeneratingAiCustom.value,
            onGenerate: handleAiCustomizationGenerate,
            onClose: _cache[4] || (_cache[4] = ($event: any) => (showAiPanel.value = false))
          }, null, 8, ["show", "trigger-el", "disabled"]),
          _createVNode(AiRefinePopover, {
            show: showAiComposePanel.value,
            "trigger-el": aiComposeButtonRef.value,
            disabled: isGeneratingAiCompose.value,
            title: "AI Compose",
            "generate-button-label": "Generate Message",
            "show-attachments": false,
            onGenerate: handleAiComposeGenerate,
            onClose: _cache[5] || (_cache[5] = ($event: any) => (showAiComposePanel.value = false))
          }, null, 8, ["show", "trigger-el", "disabled"])
        ])
      ], 32)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(Button), {
        variant: "ghost",
        size: "md",
        onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('close'))),
        disabled: __props.isLoading
      }, {
        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
          _createTextVNode(" Cancel ", -1)
        ]))]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_unref(Button), {
        type: "submit",
        form: "sms-chat-form",
        variant: "primary",
        size: "md",
        icon: "mdi:message-text-outline",
        "icon-position": "left",
        loading: __props.isLoading,
        disabled: !canSubmit.value
      }, {
        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
          _createTextVNode(" Start Conversation ", -1)
        ]))]),
        _: 1
      }, 8, ["loading", "disabled"])
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})